/**
 * AudioWorklet processor for KT1 capture, shipped as source text and loaded
 * through a Blob URL.
 *
 * Why a string: the worklet runs in AudioWorkletGlobalScope, which has no
 * `importScripts` and no Vite dev-client support, and the bundler must not
 * rewrite or inject helpers into it. Loading worklets from Blob URLs is the
 * pattern used by established Web Audio libraries on iOS Safari.
 *
 * Behaviour: down-mixes all input channels to mono, buffers 4096 frames and
 * transfers each full buffer to the main thread. A `flush` message posts the
 * remainder followed by `flushed`. No audio is written to the outputs, so the
 * node is silent even though it is connected to the destination (Safari only
 * runs processors that are connected to the graph).
 */

export const CAPTURE_PROCESSOR_NAME = 'kt1-capture'
export const CAPTURE_CHUNK_FRAMES = 4096

export type ProcessorMessage =
  { type: 'chunk'; samples: Float32Array } | { type: 'flushed' }

export const CAPTURE_PROCESSOR_SOURCE = `
class Kt1CaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.buffer = new Float32Array(${String(CAPTURE_CHUNK_FRAMES)})
    this.length = 0
    this.port.onmessage = (event) => {
      if (event.data && event.data.type === 'flush') {
        this.flush()
        this.port.postMessage({ type: 'flushed' })
      }
    }
  }

  flush() {
    if (this.length === 0) return
    const out = this.buffer.slice(0, this.length)
    this.length = 0
    this.port.postMessage({ type: 'chunk', samples: out }, [out.buffer])
  }

  process(inputs) {
    const input = inputs[0]
    if (input && input.length > 0) {
      const channels = input.length
      const frames = input[0].length
      for (let i = 0; i < frames; i++) {
        let sum = 0
        for (let c = 0; c < channels; c++) sum += input[c][i]
        this.buffer[this.length++] = sum / channels
        if (this.length === this.buffer.length) this.flush()
      }
    }
    return true
  }
}

registerProcessor('${CAPTURE_PROCESSOR_NAME}', Kt1CaptureProcessor)
`
